import React, { useState } from 'react';
import { motion } from 'framer-motion';
import TopBar from '../components/TopBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { contactDetails, centers } from '../data/siteContent';
import heroImage from '../images/patna.png';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const whatsappNumber = contactDetails.primaryPhone.replace(/\D/g, '');
    const message = [
      'New Contact Form Inquiry - Uttam Diabetes Care',
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
      `Message: ${formData.message}`,
    ].join('\n');

    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="contact-page page-load-shell">
      <SEO
        title="Contact Uttam Diabetes Care | Diabetes Consultation in Patna"
        description="Contact Uttam Diabetes Care in Patna for diabetes consultation, insulin guidance, kidney wellness support, appointment information, and Hindi-friendly assistance."
        keywords="contact diabetes doctor Patna, diabetes appointment Bihar, WhatsApp diabetes consultation Patna, kidney care contact Bihar, Hindi medical consultation Patna"
      />
      <TopBar />
      <Navbar />

      <section className="hero-section">
        <div className="hero-image">
          <img src={heroImage} alt="Contact us" />
        </div>
        <div className="hero-overlay">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Contact Us</h1>
            <p>Contact our Patna diabetes care team for appointments, kidney wellness guidance, and Hindi-friendly consultation support</p>
          </motion.div>
        </div>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <motion.div
              className="contact-form-section"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-kicker">Send inquiry</span>
              <h2>Book Diabetes Consultation on WhatsApp</h2>
              <p className="centers-disclaimer" style={{ textAlign: 'left', margin: '0 0 24px' }}>
                Submit this form to WhatsApp with your full details. The team can help with diabetes
                consultation, insulin-related questions, appointment timing, city availability, and
                kidney care follow-up guidance.
              </p>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Diabetes, insulin, kidney reports, appointment city, or follow-up question"
                    required
                  ></textarea>
                </div>
                <button type="submit" className="submit-button">Send on WhatsApp</button>
              </form>
            </motion.div>

            <motion.div
              className="contact-info-section"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-kicker">Reach us</span>
              <h2>Contact Information</h2>
              <div className="info-item">
                <i className="fas fa-map-marker-alt"></i>
                <div>
                  <h3>Primary City</h3>
                  <p>{contactDetails.address}</p>
                  <p>{contactDetails.addressNote}</p>
                </div>
              </div>
              <div className="info-item">
                <i className="fas fa-phone-alt"></i>
                <div>
                  <h3>Phone</h3>
                  <p>{contactDetails.primaryPhone}</p>
                  <p>Kushinagar listing: {contactDetails.secondaryPhone}</p>
                </div>
              </div>
              <div className="info-item">
                <i className="fas fa-envelope"></i>
                <div>
                  <h3>Email</h3>
                  <p>{contactDetails.email}</p>
                </div>
              </div>
              <div className="info-item">
                <i className="fas fa-clock"></i>
                <div>
                  <h3>Timings</h3>
                  <p>{contactDetails.hours}</p>
                </div>
              </div>
              <div className="info-item">
                <i className="fas fa-language"></i>
                <div>
                  <h3>Hindi Support</h3>
                  <p>{contactDetails.supportText}</p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="centers-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: '60px' }}
          >
            <span className="section-kicker">Consultation cities</span>
            <h2 className="section-title">Available Locations</h2>
            <p className="centers-disclaimer">
              Patna, Muzaffarpur, Motihari, Siwan, Gopalganj, Purnia, Bhagalpur, Forbesganj, Kushinagar,
              and Darbhanga.
            </p>
            <p className="centers-disclaimer">
              Total listed locations in the project: {centers.length}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="google-map-section">
        <div className="map-container">
          <iframe
            title="Patna Bihar map"
            src="https://www.google.com/maps?q=Patna%20Bihar%20800001&z=13&output=embed"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
